import React from "react";
import {
  Box,
  IconButton,
  Typography,
  alpha,
  CircularProgress,
  Divider,
  Collapse,
} from "@mui/material";
import { Close, AutoAwesome, Person } from "@mui/icons-material";

// --- props ---------------------------------------------------------
type Props = { summary: string; hlsUrl: string; onClose: () => void; sx?: object };

// NOTE: keep your env if you want; leaving your local default for now
const AI_API_URL = process.env.REACT_APP_AI_API_URL;
if (!AI_API_URL) {
  throw new Error("REACT_APP_AI_API_URL is not defined");
}

// --- tiny red "NEW" pill -------------------------------------------
const NewPill: React.FC = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.75,
      py: 0.25,
      borderRadius: 999,
      fontSize: "10px",
      fontWeight: 700,
      color: "#fff",
      bgcolor: "#ff4757",
      lineHeight: 1,
      position: "relative",
      "&::before": {
        content: '""',
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "rgba(255,255,255,0.9)",
        display: "inline-block",
        animation: "pulseDot 1.8s ease-in-out infinite",
      },
      "@keyframes pulseDot": {
        "0%, 100%": { transform: "scale(1)", opacity: 1 },
        "50%": { transform: "scale(1.5)", opacity: 0.6 },
      },
    }}
  >
    NEW
  </Box>
);

// --- component -----------------------------------------------------
export const StreamSummaryBanner = ({ summary, hlsUrl, onClose, sx }: Props) => {
  const [expanded, setExpanded] = React.useState(false);

  // X panel state (inline expansion below the banner)
  const [openXPanel, setOpenXPanel] = React.useState(false);
  const [loadingX, setLoadingX] = React.useState(false);
  const [xError, setXError] = React.useState<string | null>(null);
  type XItem = { name: string; tag: string; date: string; text: string; url: string };
  const [xItems, setXItems] = React.useState<XItem[]>([]);

  // Format like: 8:14 AM · Sep 1, 2025
  const formatXDate = (iso: string) => {
    const d = new Date(iso);
    const t = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const ds = d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${t} · ${ds}`;
  };

  const fetchReactions = async () => {
    setOpenXPanel(true);
    setLoadingX(true);
    setXError(null);
    setXItems([]);
    try {
      const url = `${AI_API_URL}/twitter/roots?hls_url=${encodeURIComponent(hlsUrl)}`;
      const resp = await fetch(url);
      const data = await resp.json().catch(() => ({}));
      const s = Number(data?.status ?? resp.status);

      if (s === 200 && Array.isArray(data?.items)) {
        setXItems(data.items as XItem[]);
      } else if (s === 429) {
        setXError("Too many requests — the X API window is exhausted. Try again shortly.");
      } else {
        setXError("Server error while searching X.");
      }
    } catch {
      setXError("Network error while contacting the server.");
    } finally {
      setLoadingX(false);
    }
  };

  const toggleExpanded = () => setExpanded((v) => !v);
  const onKeyToggle: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        pointerEvents: "auto",
        position: "fixed",
        top: { xs: 56, sm: 64 },
        left: 0,
        right: 0,
        zIndex: 1301,
        display: "flex",
        justifyContent: "center",
        px: { xs: 1, sm: 2 },
        ...sx,
        "@media (min-width:900px) and (max-height:900px)": {
          px: 1,
          top: { xs: 52, sm: 58 },
        },
      }}
    >
      {/* CARD / BANNER */}
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggleExpanded}
        onKeyDown={onKeyToggle}
        sx={{
          width: "100%",
          maxWidth: 720,
          cursor: { xs: "pointer", sm: "default" },
          background: "linear-gradient(135deg, #1a1b3a 0%, #16172d 100%)",
          border: "1px solid",
          borderColor: alpha("#6535E9", 0.3),
          borderRadius: 2,
          boxShadow: `
            0 8px 32px rgba(0, 0, 0, 0.4),
            0 2px 8px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.06)
          `,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          p: { xs: 1.5, sm: 2 },
          position: "relative",
          overflow: "visible", // allow the expander below
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            height: "2px",
            width: "100%",
            background:
              "linear-gradient(90deg, #6535E9 0%, #4E33E9 50%, #6535E9 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 3s linear infinite",
            pointerEvents: "none",
          },
          "@keyframes shimmer": {
            "0%": { backgroundPosition: "200% 0" },
            "100%": { backgroundPosition: "-200% 0" },
          },
          "@media (min-width:900px) and (max-height:900px)": {
            p: 1,
            gap: 1,
            boxShadow:
              "0 6px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
          },
        }}
      >
        {/* icon */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 0.5,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 36,
              height: 36,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(101, 53, 233, 0.3) 0%, transparent 70%)",
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 0.5 },
                "50%": { transform: "scale(1.2)", opacity: 0.8 },
              },
            }}
          />
          <AutoAwesome
            sx={{
              fontSize: { xs: 20, sm: 22 },
              color: "#6535E9",
              filter: "drop-shadow(0 0 8px rgba(101, 53, 233, 0.5))",
              zIndex: 1,
              "@media (min-width:900px) and (max-height:900px)": { fontSize: 18 },
              "@media (max-width:400px)": { fontSize: 18 },
            }}
          />
        </Box>

        {/* content */}
        <Box sx={{ flex: 1, pr: 3 }}>
          <Typography
            sx={{
              fontSize: { xs: "0.625rem", sm: "0.6875rem" },
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#6535E9",
              mb: 0.25,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Stream Update
            <Box
              component="span"
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: alpha("#6535E9", 0.2),
                borderRadius: 10,
                fontSize: "0.625rem",
                fontWeight: 600,
              }}
            >
              LIVE
            </Box>
          </Typography>

          {/* summary */}
          <Typography
            sx={[
              {
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                fontWeight: 400,
                lineHeight: 1.4,
                letterSpacing: "-0.01em",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              },
              {
                "@media (min-width:900px) and (max-height:900px)": {
                  fontSize: "0.8rem",
                  lineHeight: 1.3,
                },
              },
              !expanded && {
                "@media (max-width:400px)": {
                  fontSize: "0.8rem",
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                },
              },
            ]}
          >
            {summary}
          </Typography>

          {/* inline NEW + link-like trigger */}
          <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
            <NewPill />
            <Typography
              component="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                fetchReactions();
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  fetchReactions();
                }
              }}
              sx={{
                border: "none",
                background: "transparent",
                color: "#fff",
                opacity: 0.92,
                cursor: "pointer",
                textAlign: "left",
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                fontWeight: 600,
                px: 0,
                py: 0,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                textDecorationThickness: "from-font",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              What do people think about this topic?
            </Typography>
          </Box>
        </Box>

        {/* close */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: "absolute",
            top: { xs: 6, sm: 8 },
            right: { xs: 6, sm: 8 },
            p: { xs: 0.5, sm: 0.75 },
            color: alpha("#fff", 0.5),
            bgcolor: alpha("#000", 0.2),
            border: "1px solid",
            borderColor: alpha("#fff", 0.1),
            transition: "all 0.2s ease",
            "&:hover": {
              color: alpha("#fff", 0.8),
              bgcolor: alpha("#000", 0.4),
              borderColor: alpha("#6535E9", 0.3),
              transform: "rotate(90deg)",
            },
            "@media (min-width:900px) and (max-height:900px)": { p: 0.5 },
            "@media (max-width:400px)": { p: 0.5 },
          }}
        >
          <Close sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </IconButton>

        {/* ---------- EXPANDING PANEL (no modal; sits under banner) ---------- */}
        <Collapse
          in={openXPanel}
          timeout={300}
          unmountOnExit
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            zIndex: 1302,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", px: { xs: 0, sm: 0 } }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: 720,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha("#fff", 0.08),
                bgcolor: "rgba(22,23,45,0.98)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                overflow: "hidden",
                maxHeight: "47vh",
                position: "relative",
                zIndex: 10,
              }}
            >
              {/* header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: { xs: 0.75, sm: 1 },
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}>
                  What people are saying on X
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setOpenXPanel(false)}
                  sx={{ 
                    p: 0.25,
                    color: alpha("#fff", 0.7), 
                    "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.08) } 
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              {/* body */}
              <Box sx={{ 
                  p: 0,
                  maxHeight: "calc(47vh - 48px)", // Change from 50vh to match container's 47vh
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  pb: 1, // Add bottom padding so last item isn't cut off
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255,255,255,0.02)",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: alpha("#6535E9", 0.4),
                    borderRadius: "2px",
                    "&:hover": {
                      background: alpha("#6535E9", 0.6),
                    },
                  },
                  // Firefox
                  scrollbarWidth: "thin",
                  scrollbarColor: `${alpha("#6535E9", 0.4)} rgba(255,255,255,0.02)`,
                }}>
                {loadingX && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={16} />
                    <Typography sx={{ ml: 1, opacity: 0.7, fontSize: { xs: 11, sm: 12 } }}>
                      Fetching reactions…
                    </Typography>
                  </Box>
                )}

                {!loadingX && xError && (
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography sx={{ color: "error.light", fontSize: { xs: 11, sm: 12 } }}>{xError}</Typography>
                  </Box>
                )}

                {!loadingX && !xError && xItems.length === 0 && (
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography sx={{ opacity: 0.7, fontSize: { xs: 11, sm: 12 } }}>
                      No relevant posts found yet.
                    </Typography>
                  </Box>
                )}

                {!loadingX && !xError && xItems.length > 0 && (
                <Box>
                  {xItems.map((t, idx) => (
                    <Box key={idx}>
                      <Box
                        component="a"
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          gap: 0.75,
                          textDecoration: "none",
                          color: "inherit",
                          px: { xs: 1, sm: 1.25 },
                          py: { xs: 0.625, sm: 0.75 },
                          mx: { xs: 0.5, sm: 0.75 },
                          my: { xs: 0.25, sm: 0.375 },
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "rgba(255,255,255,0.03)",
                          bgcolor: "rgba(255,255,255,0.01)",
                          transition: "all 0.2s",
                          // Add fade-in animation
                          opacity: 0,
                          animation: `fadeIn 0.4s ease-out ${idx * 0.1}s forwards`,
                          "@keyframes fadeIn": {
                            from: {
                              opacity: 0,
                              transform: "translateY(10px)",
                            },
                            to: {
                              opacity: 1,
                              transform: "translateY(0)",
                            },
                          },
                          "&:hover": { 
                            bgcolor: "rgba(255,255,255,0.04)",
                            borderColor: alpha("#6535E9", 0.15),
                            transform: "translateX(2px)",
                          },
                        }}
                      >
                        {/* Avatar - smaller */}
                        <Box
                          sx={{
                            width: 24,  // Reduced from 32
                            height: 24,  // Reduced from 32
                            borderRadius: "50%",
                            bgcolor: alpha("#1DA1F2", 0.15),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            border: "1px solid",
                            borderColor: alpha("#fff", 0.08),
                          }}
                        >
                          <Person sx={{ fontSize: 12, color: alpha("#fff", 0.5) }} />  {/* Reduced from 16 */}
                        </Box>

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Header: name and handle */}
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4, mb: 0.15 }}>  {/* Reduced gap and mb */}
                            <Typography 
                              sx={{ 
                                fontWeight: 600, 
                                fontSize: { xs: "0.625rem", sm: "0.6875rem" },  // Reduced from 0.6875/0.75
                                lineHeight: 1.1,
                              }}
                            >
                              {t.name}
                            </Typography>
                            <Typography 
                              sx={{ 
                                color: alpha("#fff", 0.5), 
                                fontSize: { xs: "0.625rem", sm: "0.6875rem" },  // Reduced
                                lineHeight: 1.1
                              }}
                            >
                              {t.tag}
                            </Typography>
                          </Box>

                          {/* Tweet text */}
                          <Typography
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: { xs: "0.6875rem", sm: "0.75rem" },  // Reduced from 0.75/0.8125
                              lineHeight: 1.25,  // Tighter line height
                              color: "rgba(255,255,255,0.87)",
                            }}
                          >
                            {t.text}
                          </Typography>

                          {/* Date */}
                          <Typography
                            sx={{ 
                              mt: 0.25,  // Reduced from 0.5
                              color: alpha("#fff", 0.5), 
                              fontSize: { xs: "0.5625rem", sm: "0.625rem" }  // Reduced from 0.625/0.6875
                            }}
                          >
                            {formatXDate(t.date)}
                          </Typography>
                        </Box>
                      </Box>
                      {idx < xItems.length - 1 && (
                        <Box 
                          sx={{ 
                            mx: { xs: 1, sm: 1.5 },
                            my: { xs: 0.375, sm: 0.5 },
                          }}
                        >
                          <Divider 
                            sx={{ 
                              borderColor: alpha("#6535E9", 0.3),  // Violet color matching scrollbar
                              borderWidth: "0.5px",  // Thin line
                              opacity: 0.6,
                            }} 
                          />
                        </Box>
                      )}

                    </Box>
                  ))}
                </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};
